/**
 * Data transformation utility functions
 */

/**
 * Build location map from locations array
 * @param {Array} locations - Array of location objects
 * @returns {Object} Map of locationId -> locationName
 */
export const buildLocationMap = (locations) => {
  const map = {}
  locations.forEach(loc => {
    if (loc.id && loc.name) {
      map[loc.id] = loc.name
    }
  })
  return map
}

/**
 * Build field name map from fields array
 * @param {Array} fields - Array of field objects
 * @param {Object} defaultMap - Default field name map
 * @returns {Object} Map of fieldId -> fieldName
 */
export const buildFieldNameMap = (fields, defaultMap = {}) => {
  const map = { ...defaultMap }
  fields.forEach(field => {
    const fieldId = field.id || field.fieldId
    const fieldLabel = field.label || field.name || field.displayName
    if (fieldId && fieldLabel && !map[fieldId]) {
      map[fieldId] = fieldLabel
    }
  })
  return map
}

/**
 * Group items by a key
 * @param {Array} items - Array of items to group
 * @param {string|Function} keyOrFn - Key name or function to get group key
 * @returns {Object} Grouped items
 */
export const groupBy = (items, keyOrFn) => {
  return items.reduce((groups, item) => {
    const key = typeof keyOrFn === 'function' ? keyOrFn(item) : item[keyOrFn]
    if (!groups[key]) {
      groups[key] = []
    }
    groups[key].push(item)
    return groups
  }, {})
}

/**
 * Filter items by search query
 * @param {Array} items - Array of items to filter
 * @param {string} query - Search query
 * @param {Array} searchKeys - Keys to search in
 * @returns {Array} Filtered items
 */
export const filterBySearch = (items, query, searchKeys = []) => {
  if (!query || !query.trim()) return items

  const lowerQuery = query.toLowerCase().trim()

  return items.filter(item => {
    return searchKeys.some(key => {
      const value = item[key]
      if (value === null || value === undefined) return false
      return String(value).toLowerCase().includes(lowerQuery)
    })
  })
}

/**
 * Transform subcontract to report format
 * @param {Object} subcontract - Subcontract object
 * @returns {Object} Transformed report object
 */
export const transformSubcontractToReport = (subcontract) => {
  return {
    reportId: subcontract.id,
    id: subcontract.id,
    code: subcontract.code,
    name: subcontract.name,
    supplier: subcontract.supplier,
    status: subcontract.status,
    compliance: subcontract.compliance || 'N/A',
    complianceDetails: subcontract.complianceDetails || {
      totalRequirements: 0,
      fulfilledRequirements: 0,
      pendingRequirements: 0
    },
    workflow: subcontract.workflow,
    budgetAllocated: subcontract.budgetAllocated,
    createdAt: subcontract.createdAt,
    updatedAt: subcontract.updatedAt,
    fields: [
      { id: 'code', label: 'Code', value: subcontract.code },
      { id: 'name', label: 'Name', value: subcontract.name },
      { id: 'supplier', label: 'Supplier', value: subcontract.supplier },
      { id: 'status', label: 'Status', value: subcontract.status },
      { id: 'compliance', label: 'Compliance', value: subcontract.compliance || 'N/A' }
    ],
    _isSubcontract: true
  }
}

/**
 * Extract unique values from array of objects
 * @param {Array} items - Array of items
 * @param {string} key - Key to extract values from
 * @returns {Array} Unique values
 */
export const getUniqueValues = (items, key) => {
  const values = items.map(item => item[key]).filter(Boolean)
  return [...new Set(values)]
}

/**
 * Deep clone an object
 * @param {*} obj - Object to clone
 * @returns {*} Cloned object
 */
export const deepClone = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj
  if (obj instanceof Date) return new Date(obj)
  if (obj instanceof Array) return obj.map(item => deepClone(item))

  const clonedObj = {}
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      clonedObj[key] = deepClone(obj[key])
    }
  }
  return clonedObj
}

/**
 * Merge objects deeply
 * @param {Object} target - Target object
 * @param {Object} source - Source object
 * @returns {Object} Merged object
 */
export const deepMerge = (target, source) => {
  const output = { ...target }

  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          output[key] = source[key]
        } else {
          output[key] = deepMerge(target[key], source[key])
        }
      } else {
        output[key] = source[key]
      }
    })
  }

  return output
}

/**
 * Check if value is a plain object
 * @param {*} item - Value to check
 * @returns {boolean} True if plain object
 */
const isObject = (item) => {
  return item && typeof item === 'object' && !Array.isArray(item)
}
